import {
  AcUnitRounded as AcUnitRoundedIcon,
  Inventory2Outlined as Inventory2OutlinedIcon,
  LayersOutlined as LayersOutlinedIcon,
  TaskAltRounded as TaskAltRoundedIcon,
  WarningAmberRounded as WarningAmberRoundedIcon,
} from '@mui/icons-material';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type DotItemDotProps,
} from 'recharts';
import moment from 'moment';
import styled from 'styled-components';
import { colors, containerTypes, DEPOT_OCCUPANCY_TREND_MOCK_DATA } from '../../const';
import { DonutPanel, Typography } from '../../design-system';
import { i18n } from '../../i18n';
import { OverviewQuery } from '../../types';
import { forecastValuesByTrend } from './utils';

const Card = styled.div`
  display: grid;
  grid-template-rows: max-content 1fr;
  row-gap: 1.6rem;
  min-width: 0;
  padding: 1.6rem;
  background: var(--white);
  border: 0.1rem solid var(--gray-50);
  border-radius: var(--border-radius-6);
`;

const Chart = styled.div`
  width: 100%;
  min-width: 0;
  min-height: 22rem;
  height: 100%;
`;

const SummaryCard = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 1.2rem;
  align-items: center;
  min-width: max-content;
  padding: 1.6rem;
  background: var(--white);
  border: 0.1rem solid var(--gray-50);
  border-radius: var(--border-radius-6);
`;

const SummaryIcon = styled.div<{ $background: string; $color: string }>`
  display: grid;
  place-items: center;
  width: 4.8rem;
  height: 4.8rem;
  color: ${({ $color }) => $color};
  background: ${({ $background }) => $background};
  border-radius: var(--border-radius-6);

  svg {
    font-size: 2.8rem;
  }
`;

const SummaryContent = styled.div`
  display: grid;
  row-gap: 0.6rem;
  min-width: max-content;
  white-space: nowrap;
`;

const SummaryValueRow = styled.div`
  display: grid;
  grid-template-columns: max-content max-content;
  column-gap: 0.8rem;
  align-items: baseline;
  min-width: max-content;
  white-space: nowrap;

  > * {
    white-space: nowrap;
  }
`;

const ProgressTrack = styled.div<{ $invisible?: boolean }>`
  height: 0.6rem;
  overflow: hidden;
  background: var(--gray-40);
  border-radius: 0.6rem;
  visibility: ${({ $invisible }) => ($invisible ? 'hidden' : 'visible')};
`;

const ProgressValue = styled.div<{ $color: string; $percent: number }>`
  width: ${({ $percent }) => `${$percent}%`};
  height: 100%;
  background: ${({ $color }) => $color};
  border-radius: inherit;
`;

const AreaRows = styled.div`
  display: grid;
  grid-template-columns: max-content minmax(24rem, 1fr) 4rem max-content;
  column-gap: 1.2rem;
  align-content: center;
  align-items: center;
  row-gap: 1.6rem;
`;

const AreaRow = styled.div`
  display: contents;
`;

const AreaBar = styled.div`
  height: 1.8rem;
  overflow: hidden;
  background: var(--gray-40);
  border-radius: 0.4rem;
`;

const summaryStyles = {
  capacity: {
    color: colors['--gray-80'],
    background: colors['--gray-40'],
    icon: <LayersOutlinedIcon />,
  },
  occupied: {
    color: colors['--primary-100'],
    background: colors['--primary-40'],
    icon: <Inventory2OutlinedIcon />,
  },
  available: {
    color: colors['--green-100'],
    background: colors['--green-40'],
    icon: <TaskAltRoundedIcon />,
  },
  reefer: {
    color: colors['--primary-80'],
    background: colors['--primary-40'],
    icon: <AcUnitRoundedIcon />,
  },
  hazardous: {
    color: colors['--red-100'],
    background: colors['--red-40'],
    icon: <WarningAmberRoundedIcon />,
  },
};

const areaColors = [
  colors['--red-100'],
  colors['--yellow-100'],
  colors['--primary-100'],
  colors['--green-100'],
  colors['--secondary-100'],
  colors['--gray-70'],
];

const CHART_INITIAL_DIMENSION = { width: 420, height: 220 } as const;

type DepotOccupancyData = OverviewQuery['data']['depotOccupancy'];

const PanelTitle = ({ children }: { children: string }) => (
  <Typography variant="textM" weight="semibold" color="--gray-100">
    {children}
  </Typography>
);

interface DepotOccupancyPanelProps {
  data: DepotOccupancyData;
}

interface DepotOccupancyTrendPanelProps extends DepotOccupancyPanelProps {
  lastDays: number;
  isDemo?: boolean;
}

const OccupancyTrendDot = ({ cx, cy, payload }: DotItemDotProps) => {
  if (cx === undefined || cy === undefined) return null;

  const color = payload.value > 100 ? colors['--red-100'] : colors['--primary-100'];

  return <circle cx={cx} cy={cy} r={3} fill={color} stroke={colors['--white']} strokeWidth={1} />;
};

export const DepotOccupancySummaryPanels = ({ data }: DepotOccupancyPanelProps) => {
  const summary = [
    {
      type: 'capacity',
      label: i18n.t('pages.overview.depotOccupancy.totalCapacity'),
      value: data.totalCapacity,
      capacity: data.totalCapacity,
    },
    {
      type: 'occupied',
      label: i18n.t('pages.overview.depotOccupancy.occupied'),
      value: data.occupied,
      capacity: data.totalCapacity,
    },
    {
      type: 'available',
      label: i18n.t('pages.overview.depotOccupancy.available'),
      value: data.available,
      capacity: data.totalCapacity,
    },
    {
      type: 'reefer',
      label: i18n.t('pages.overview.depotOccupancy.reeferOccupancyDemo'),
      value: 320,
      capacity: 500,
    },
    {
      type: 'hazardous',
      label: i18n.t('pages.overview.depotOccupancy.hazardousDemo'),
      value: 120,
      capacity: 300,
    },
  ] as const;

  return (
    <>
      {summary.map((item) => {
        const percent = item.capacity ? Math.round((item.value / item.capacity) * 1000) / 10 : 0;
        const style = summaryStyles[item.type];
        const isCapacity = item.type === 'capacity';

        return (
          <SummaryCard key={item.type}>
            <SummaryIcon $background={style.background} $color={style.color}>
              {style.icon}
            </SummaryIcon>
            <SummaryContent>
              <Typography variant="textS" color="--gray-80">
                {item.label}
              </Typography>
              <SummaryValueRow>
                <Typography variant="textL" weight="semibold" color="--gray-100">
                  {['capacity', 'occupied', 'available'].includes(item.type)
                    ? `${item.value.toLocaleString('en-US')} ${i18n.t(
                        'pages.overview.depotOccupancy.unitTeu',
                      )}`
                    : `${item.value.toLocaleString('en-US')} / ${item.capacity.toLocaleString(
                        'en-US',
                      )}`}
                </Typography>
                <Typography variant="textS" color="--gray-70">
                  {percent.toFixed(1)}%
                </Typography>
              </SummaryValueRow>
              <ProgressTrack $invisible={isCapacity}>
                <ProgressValue
                  $color={style.color}
                  $percent={Math.min(Math.max(percent, 0), 100)}
                />
              </ProgressTrack>
            </SummaryContent>
          </SummaryCard>
        );
      })}
    </>
  );
};

export const DepotOccupancyByAreaPanel = ({ data }: DepotOccupancyPanelProps) => (
  <Card>
    <PanelTitle>{i18n.t('pages.overview.depotOccupancy.areaTitle')}</PanelTitle>
    <AreaRows>
      {data.occupancyByArea.map((item, index) => {
        const percent = item.capacity ? Math.round((item.occupied / item.capacity) * 100) : 0;
        return (
          <AreaRow key={item.id}>
            <Typography variant="textS" color="--gray-90">
              {item.block}
            </Typography>
            <AreaBar>
              <ProgressValue
                $color={areaColors[index % areaColors.length]}
                $percent={Math.min(Math.max(percent, 0), 100)}
              />
            </AreaBar>
            <Typography variant="textS" weight="semibold" color="--gray-90">
              {percent}%
            </Typography>
            <Typography variant="textS" color="--gray-70" align="right">
              {item.occupied.toLocaleString('en-US')} / {item.capacity.toLocaleString('en-US')}{' '}
              {i18n.t('pages.overview.depotOccupancy.unitTeu')}
            </Typography>
          </AreaRow>
        );
      })}
    </AreaRows>
  </Card>
);

export const DepotOccupancyByContainerTypePanel = ({ data }: DepotOccupancyPanelProps) => (
  <DonutPanel
    title={i18n.t('pages.overview.depotOccupancy.containerTypeTitle')}
    categories={[
      ...containerTypes.map(({ label, value }) => ({
        name: label,
        value: data.occupancyByContainerType[value],
      })),
      {
        name: i18n.t('pages.overview.depotOccupancy.empty'),
        value: data.occupancyByContainerType.empty,
      },
    ]}
    centerLabel={i18n.t('pages.overview.depotOccupancy.unitTeu')}
  />
);

export const DepotOccupancyTrendPanel = ({
  data,
  lastDays,
  isDemo = false,
}: DepotOccupancyTrendPanelProps) => {
  const historyDays = Math.max(1, Math.ceil(lastDays / 2));
  const forecastDays = Math.max(1, lastDays - historyDays);
  const history = isDemo
    ? Array.from({ length: historyDays }, (_, index) => ({
        date: moment()
          .subtract(historyDays - index - 1, 'day')
          .format('YYYY-MM-DD'),
        value: Math.round(
          DEPOT_OCCUPANCY_TREND_MOCK_DATA.historyStartPercent +
            ((DEPOT_OCCUPANCY_TREND_MOCK_DATA.currentPercent -
              DEPOT_OCCUPANCY_TREND_MOCK_DATA.historyStartPercent) *
              index) /
              Math.max(historyDays - 1, 1),
        ),
      }))
    : data.occupancyTrend.slice(-historyDays);
  const forecast = isDemo
    ? Array.from({ length: forecastDays }, (_, index) =>
        Math.round(
          DEPOT_OCCUPANCY_TREND_MOCK_DATA.currentPercent +
            ((DEPOT_OCCUPANCY_TREND_MOCK_DATA.forecastEndPercent -
              DEPOT_OCCUPANCY_TREND_MOCK_DATA.currentPercent) *
              (index + 1)) /
              forecastDays,
        ),
      )
    : forecastValuesByTrend(
        history.map((item) => item.value),
        forecastDays,
      );
  const lastHistoryDate = history.at(-1)?.date ?? moment().format('YYYY-MM-DD');
  const trendData = history.map((item) => ({
    day: moment(item.date).format('MMM-D'),
    value: item.value,
  }));

  forecast.forEach((value, index) => {
    trendData.push({
      day: moment(lastHistoryDate)
        .add(index + 1, 'day')
        .format('MMM-D'),
      value,
    });
  });

  const trendValues = trendData.map((item) => item.value);
  const minimumTrendValue = Math.min(...trendValues);
  const maximumTrendValue = Math.max(...trendValues);
  const chartMaximum = Math.max(100, Math.ceil(maximumTrendValue / 10) * 10);
  const areaCapacityOffset =
    maximumTrendValue > 100 ? ((maximumTrendValue - 100) / maximumTrendValue) * 100 : 0;
  const strokeCapacityOffset =
    maximumTrendValue <= 100
      ? 0
      : minimumTrendValue >= 100
      ? 100
      : ((maximumTrendValue - 100) / (maximumTrendValue - minimumTrendValue)) * 100;
  const trendStroke =
    maximumTrendValue <= 100
      ? colors['--primary-100']
      : minimumTrendValue >= 100
      ? colors['--red-100']
      : 'url(#depot-trend-stroke)';

  return (
    <Card>
      <PanelTitle>{i18n.t('pages.overview.depotOccupancy.trendTitle')}</PanelTitle>
      <Chart>
        <ResponsiveContainer width="100%" height="100%" initialDimension={CHART_INITIAL_DIMENSION}>
          <AreaChart data={trendData} margin={{ top: 8, right: 8, left: -24 }}>
            <defs>
              <linearGradient id="depot-trend-fill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={colors['--red-100']} stopOpacity={0.25} />
                <stop
                  offset={`${areaCapacityOffset}%`}
                  stopColor={colors['--red-100']}
                  stopOpacity={0.25}
                />
                <stop
                  offset={`${areaCapacityOffset}%`}
                  stopColor={colors['--primary-100']}
                  stopOpacity={0.25}
                />
                <stop offset="100%" stopColor={colors['--primary-100']} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="depot-trend-stroke" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={colors['--red-100']} />
                <stop offset={`${strokeCapacityOffset}%`} stopColor={colors['--red-100']} />
                <stop offset={`${strokeCapacityOffset}%`} stopColor={colors['--primary-100']} />
                <stop offset="100%" stopColor={colors['--primary-100']} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={colors['--gray-40']} />
            <XAxis
              dataKey="day"
              interval="preserveStartEnd"
              tickLine={false}
              tick={{ fill: colors['--gray-70'] }}
            />
            <YAxis
              domain={[0, chartMaximum]}
              tickLine={false}
              tick={{ fill: colors['--gray-70'] }}
              unit="%"
            />
            <Tooltip />
            <Area
              name={i18n.t('pages.overview.depotOccupancy.occupancy')}
              type="monotone"
              dataKey="value"
              stroke={trendStroke}
              strokeWidth={2}
              fill="url(#depot-trend-fill)"
              dot={OccupancyTrendDot}
              activeDot={false}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Chart>
    </Card>
  );
};
